import { useEffect, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Center, Loader, Stack, Text } from '@mantine/core';
import { Gauge } from 'lucide-react';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { useSurface } from '@/shared/surfaces/useSurface';
import TopicNotFound from '@/shared/surfaces/TopicNotFound';
import PageLevelFilterRow from '@/shared/surfaces/PageLevelFilterRow';
import SurfaceAssistScope, { SurfaceAssistToolbar } from '@/shared/surfaces/assist/SurfaceAssistScope';
import { useAssistFilters } from '@/shared/surfaces/assist/AssistSurfaceProvider';
import type { AnalyticsSurface } from '@/shared/surfaces/AnalyticsSurface';
import { AdoptionViewModel } from './AdoptionViewModel';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import AdoptionScoreHero from './AdoptionScoreHero';
import AdoptionDriverTiles from './AdoptionDriverTiles';

/**
 * Adoption overview: one composite score for how fully the org has embraced AI,
 * built from four drivers. The hero gauge clicks through to the drivers &
 * trends detail; the four driver tiles summarise each signal.
 */
export default function AdoptionPage(): ReactElement {
    const surface = useSurface('behavior');
    if (surface === undefined) return <TopicNotFound />;
    return (
        <SurfaceAssistScope surface={surface}>
            <AdoptionOverviewBody surface={surface} />
        </SurfaceAssistScope>
    );
}

function AdoptionOverviewBody({ surface }: { surface: AnalyticsSurface }): ReactElement {
    const vm = useViewModel(AdoptionViewModel);
    const navigate = useNavigate();
    const ambientFilters = useAssistFilters();

    useEffect(() => {
        vm.setAmbientFilters([...ambientFilters]);
        void vm.loadOverview();
    }, [vm, ambientFilters]);

    return (
        <>
            <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

            <PageHeaderComponent
                icon={Gauge}
                eyebrow="Adoption"
                title="Overview"
                subtitle="One score for how fully the organization has embraced AI, built from four signals. Click through to see the drivers and trends."
            />

            {vm.loading ? (
                <Center py="xl">
                    <Loader />
                </Center>
            ) : vm.score === null ? (
                <Center py="xl">
                    <Text c="dimmed">No adoption data available for the current filters.</Text>
                </Center>
            ) : (
                <Stack gap="md">
                    <AdoptionScoreHero score={vm.score} breakdown={vm.breakdown} onOpenDrivers={() => navigate('/adoption/drivers')} />
                    <AdoptionDriverTiles drivers={vm.score.drivers} />
                </Stack>
            )}
        </>
    );
}
